import {
  getWriteApprovalThreadReviewPreview,
  type WriteApprovalEntityKind,
} from "@/components/assistant/formatWriteApprovalSummary";
import type { ApprovalBarProps } from "@/components/assistant/useAssistantWriteApprovalHandlers";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { FileText, Loader2 } from "lucide-react";
import { useMemo } from "react";

type Props = ApprovalBarProps & {
  kind: WriteApprovalEntityKind;
  mode: "create" | "update";
  approvalReady?: boolean;
  proposal: Record<string, unknown>;
  submitDisabled?: boolean;
};

export function AssistantWriteApprovalThreadMessage({
  kind,
  mode,
  approvalReady = true,
  disabled = false,
  submitDisabled = false,
  proposal,
  onCreate,
  onViewPlan,
}: Props) {
  const preview = useMemo(
    () => getWriteApprovalThreadReviewPreview(kind, proposal),
    [kind, proposal]
  );

  return (
    <div
      className={cn(
        "w-full overflow-hidden rounded-xl border border-[rgba(15,23,42,0.09)]",
        "bg-white/80 dark:border-white/10 dark:bg-white/[0.04]"
      )}
      role="region"
      aria-label="Review proposal"
    >
      <div className="flex items-center justify-between gap-2 border-b border-[rgba(15,23,42,0.09)] px-3 py-2 dark:border-white/10">
        <div className="flex min-w-0 items-center gap-2">
          <FileText className="h-3.5 w-3.5 shrink-0 text-[#576274] dark:text-slate-400" />
          <p className="text-[11px] font-medium uppercase tracking-wide text-[var(--lm-muted)] dark:text-slate-400">
            Review proposal
          </p>
        </div>
        {preview.shortCode ? (
          <span className="shrink-0 rounded-md bg-[rgba(139,92,246,0.12)] px-2 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-wide text-[#6d28d9] dark:bg-purple-500/20 dark:text-purple-300">
            {preview.shortCode}
          </span>
        ) : null}
      </div>

      <div className="space-y-2 px-3 py-2.5">
        {preview.title ? (
          <p className="text-sm font-semibold leading-snug text-[var(--lm-foreground-strong)] dark:text-slate-100">
            {preview.title}
          </p>
        ) : null}
        {preview.purposeLine ? (
          <p className="text-sm leading-relaxed text-[#576274] dark:text-gray-300">
            <span className="font-semibold text-[#101828] dark:text-slate-200">Purpose: </span>
            {preview.purposeLine}
          </p>
        ) : null}
      </div>

      <div
        className={cn(
          "flex flex-wrap items-center justify-end gap-2 border-t border-[rgba(15,23,42,0.09)] px-3 py-2 dark:border-white/10",
          !approvalReady && "opacity-90"
        )}
      >
        {!approvalReady ? (
          <div className="mr-auto flex min-w-0 items-center gap-2 text-xs text-[#576274] dark:text-gray-400">
            <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" />
            <span className="truncate">Preparing approval…</span>
          </div>
        ) : null}
        <Button
          type="button"
          variant="surface"
          size="xs"
          disabled={disabled}
          onClick={onViewPlan}
        >
          View Plan
        </Button>
        <Button
          type="button"
          variant="default"
          size="xs"
          disabled={disabled || submitDisabled}
          onClick={onCreate}
        >
          {mode === "create" ? "Create" : "Update"}
        </Button>
      </div>
    </div>
  );
}
